// Reusable live pitch-trainer core: FREE tuner mode (no target — note, Hz and
// sharp/flat of the nearest note) and TARGET mode (match a chosen/heard note).
// Built to be reused later by Match My Pitch / Sing the Written Note / interval
// and phrase activities — those must NOT be built inside this component.
// Safeguarding: uses pitch.js, which analyses the microphone live only — nothing
// is recorded, saved or uploaded, ever.
import { useEffect, useRef, useState } from "react";
import { Mic, Square, Volume2, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { usePitch, MIC_NOTE } from "../lib/pitch";
import { midiToName, midiToFreq, centsFromTarget, TARGET_MIDIS, IN_TUNE_CENTS, HOLD_MS, DEFAULT_A4 } from "../lib/tuning";

const METER_RANGE = 50; // meter shows ±50 cents
const FRAME_MS = 66;    // ~15 display updates per second (smooth, calm needle)
const TRACE_MS = 9000;  // pitch trace window

// Play a gentle reference tone (triangle wave, 1.4 s) — local synth, no files.
function playTone(midi, refA4) {
  const AC = window.AudioContext || window.webkitAudioContext;
  const ac = new AC();
  const o = ac.createOscillator();
  const g = ac.createGain();
  o.type = "triangle";
  o.frequency.value = midiToFreq(midi, refA4);
  g.gain.setValueAtTime(0.0001, ac.currentTime);
  g.gain.exponentialRampToValueAtTime(0.22, ac.currentTime + 0.04);
  g.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + 1.4);
  o.connect(g).connect(ac.destination);
  o.start();
  o.stop(ac.currentTime + 1.5);
  o.onended = () => ac.close().catch(() => {});
}

export function PitchTrainerCore({ refA4 = DEFAULT_A4, initialTarget = 62, allowTargetSelect = true, onSuccess }) {
  const { pitch, error, start, stop, listening } = usePitch();
  const [mode, setMode] = useState("free"); // free | target
  const [target, setTarget] = useState(initialTarget);
  const [disp, setDisp] = useState(null);   // { name, cents, hz, midiFloat }
  const [inTune, setInTune] = useState(false);
  const [heardRecently, setHeardRecently] = useState(true);
  const [direction, setDirection] = useState("steady");
  const hist = useRef([]);
  const trace = useRef([]);                 // [{t, midiFloat}]
  const stableMidi = useRef(null);
  const octaveStreak = useRef(0);
  const sinceInTune = useRef(null);
  const lastVoiceAt = useRef(null);
  const lastPaint = useRef(0);
  const successFired = useRef(false);
  const canvasRef = useRef(null);

  const targetMode = allowTargetSelect && mode === "target";

  useEffect(() => { // new target/mode resets the success state
    sinceInTune.current = null;
    successFired.current = false;
    setInTune(false);
  }, [target, mode]);

  useEffect(() => { // stopping resets the display to idle
    if (!listening) {
      setDisp(null);
      setInTune(false);
      setHeardRecently(true);
      setDirection("steady");
      hist.current = [];
      trace.current = [];
      stableMidi.current = null;
      octaveStreak.current = 0;
      sinceInTune.current = null;
      lastVoiceAt.current = null;
      drawTrace(true);
    }
  }, [listening]); // eslint-disable-line react-hooks/exhaustive-deps

  function drawTrace(clear = false) {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    const W = cv.width, H = cv.height;
    ctx.clearRect(0, 0, W, H);
    if (clear) return;
    const pts = trace.current;
    if (pts.length < 2) return;
    const midis = pts.map((p) => p.midiFloat);
    let lo = Math.min(...midis), hi = Math.max(...midis);
    if (targetMode) { lo = Math.min(lo, target); hi = Math.max(hi, target); }
    const pad = 0.6;
    lo -= pad; hi += pad;
    if (hi - lo < 2) { lo -= 1; hi += 1; }
    const now = pts[pts.length - 1].t;
    // target line in target mode
    if (targetMode) {
      const ty = H - ((target - lo) / (hi - lo)) * H;
      ctx.strokeStyle = "#2D6A4F";
      ctx.setLineDash([6, 5]);
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(0, ty); ctx.lineTo(W, ty); ctx.stroke();
      ctx.setLineDash([]);
    }
    ctx.strokeStyle = "#0B192C";
    ctx.lineWidth = 3;
    ctx.lineJoin = "round";
    ctx.beginPath();
    pts.forEach((p, i) => {
      const x = ((p.t - (now - TRACE_MS)) / TRACE_MS) * W;
      const y = H - ((p.midiFloat - lo) / (hi - lo)) * H;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.stroke();
  }

  useEffect(() => {
    if (!listening) return;
    if (!pitch) {
      if (lastVoiceAt.current && performance.now() - lastVoiceAt.current > 2500) setHeardRecently(false);
      return;
    }
    lastVoiceAt.current = performance.now();
    setHeardRecently(true);
    const now = performance.now();
    if (now - lastPaint.current < FRAME_MS) return;
    lastPaint.current = now;
    const mfRaw = 69 + 12 * Math.log2(pitch.freq / refA4);
    // median smoothing over recent frames kills flicker
    const h = hist.current;
    h.push(mfRaw);
    if (h.length > 7) h.shift();
    const sorted = [...h].sort((a, b) => a - b);
    let mf = sorted[Math.floor(sorted.length / 2)];
    // octave-jump guard: a full-octave leap must persist 3 frames before we believe it
    if (stableMidi.current !== null && Math.abs(Math.round(mf) - stableMidi.current) === 12) {
      octaveStreak.current += 1;
      if (octaveStreak.current < 3) mf = stableMidi.current;
    } else {
      octaveStreak.current = 0;
    }
    stableMidi.current = Math.round(mf);

    // pitch trace + rise/fall direction
    trace.current.push({ t: now, midiFloat: mf });
    while (trace.current.length && trace.current[0].t < now - TRACE_MS) trace.current.shift();
    const tr = trace.current;
    if (tr.length >= 6) {
      const slope = tr[tr.length - 1].midiFloat - tr[tr.length - 6].midiFloat;
      setDirection(slope > 0.18 ? "rising" : slope < -0.18 ? "falling" : "steady");
    }

    // cents: target mode measures against the target; free mode against the nearest note
    const nearest = Math.round(mf);
    const cents = targetMode
      ? centsFromTarget(pitch.freq * Math.pow(2, (mf - mfRaw) / 12), target, refA4)
      : Math.round((mf - nearest) * 100);
    setDisp({ name: midiToName(targetMode ? mf : nearest), cents, hz: pitch.freq, midiFloat: mf });

    if (Math.abs(cents) <= IN_TUNE_CENTS) {
      if (!sinceInTune.current) sinceInTune.current = now;
      if (now - sinceInTune.current >= HOLD_MS) {
        setInTune(true);
        if (targetMode && !successFired.current) { successFired.current = true; onSuccess?.(); }
      }
    } else {
      sinceInTune.current = null;
      successFired.current = false;
      setInTune(false);
    }
    drawTrace();
  }, [pitch, listening, refA4, target, targetMode, onSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const cents = disp ? disp.cents : null;
  const meterPct = cents === null ? 50 : 50 + Math.max(-METER_RANGE, Math.min(METER_RANGE, cents)) / METER_RANGE * 50;
  const feedback = !disp ? ""
    : inTune ? "In tune — lovely!"
    : cents < 0 ? "A little flat — sing a touch higher"
    : "A little sharp — sing a touch lower";
  const DirIcon = direction === "rising" ? TrendingUp : direction === "falling" ? TrendingDown : Minus;

  if (error === "denied") {
    return (
      <div className="rounded-2xl border-2 border-[#A62B2B]/40 bg-[#FBF0F2] p-6 space-y-3" data-testid="pitch-trainer-denied">
        <h2 className="font-display text-lg font-bold text-[#7A1C2E]">The microphone is switched off</h2>
        <p className="text-sm text-[#1C242B]">ChoirQuest needs the microphone to hear your pitch — nothing is ever recorded or saved.
          On iPhone: tap the <strong>aA</strong> icon (or padlock) in the address bar → Website Settings → Microphone → Allow, then try again.</p>
        <button onClick={start} data-testid="pitch-trainer-retry-btn"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#0B192C] text-[#FAF7EE] font-bold text-sm min-h-[48px]">
          <Mic className="w-4 h-4" aria-hidden /> Try again
        </button>
      </div>
    );
  }
  if (error === "busy") {
    return (
      <div className="rounded-2xl border-2 border-[#A62B2B]/40 bg-[#FBF0F2] p-6 space-y-3" data-testid="pitch-trainer-busy">
        <h2 className="font-display text-lg font-bold text-[#7A1C2E]">The microphone is busy</h2>
        <p className="text-sm text-[#1C242B]">Another app or browser tab is using the microphone. Close it, then try again.</p>
        <button onClick={start} data-testid="pitch-trainer-retry-btn"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#0B192C] text-[#FAF7EE] font-bold text-sm min-h-[48px]">
          <Mic className="w-4 h-4" aria-hidden /> Try again
        </button>
      </div>
    );
  }
  if (error === "unavailable") {
    return (
      <div className="rounded-2xl border-2 border-[#A62B2B]/40 bg-[#FBF0F2] p-6" data-testid="pitch-trainer-unavailable">
        <h2 className="font-display text-lg font-bold text-[#7A1C2E]">No microphone found</h2>
        <p className="text-sm text-[#1C242B]">This device does not seem to have a microphone available. Try another device, or ask your choir director for help.</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border-2 border-[#C69B3D]/50 bg-white p-5 sm:p-6 space-y-5" data-testid="pitch-trainer-core">
      {allowTargetSelect && (
        <div className="flex gap-2" data-testid="pitch-trainer-modes">
          <button onClick={() => setMode("free")} data-testid="pitch-mode-free"
            className={`flex-1 px-4 py-3 rounded-xl border-2 font-bold text-sm min-h-[48px] transition-colors ${
              !targetMode ? "border-[#C69B3D] bg-[#F6EED8] text-[#0B192C]" : "border-[#E5DEC9] bg-white text-[#566270] hover:border-[#C69B3D]"}`}>
            Free tuning
          </button>
          <button onClick={() => setMode("target")} data-testid="pitch-mode-target"
            className={`flex-1 px-4 py-3 rounded-xl border-2 font-bold text-sm min-h-[48px] transition-colors ${
              targetMode ? "border-[#C69B3D] bg-[#F6EED8] text-[#0B192C]" : "border-[#E5DEC9] bg-white text-[#566270] hover:border-[#C69B3D]"}`}>
            Match a note
          </button>
        </div>
      )}

      {targetMode && (
        <div>
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <p className="text-xs font-bold uppercase tracking-widest text-[#8a6a10]">Target note: {midiToName(target)}</p>
            <button onClick={() => playTone(target, refA4)} data-testid="hear-target-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#0B192C] text-[#FAF7EE] font-bold text-xs min-h-[44px]">
              <Volume2 className="w-3.5 h-3.5" aria-hidden /> Hear it
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {TARGET_MIDIS.map((m) => (
              <button key={m} onClick={() => setTarget(m)} data-testid={`target-note-${m}`}
                className={`px-4 py-2.5 rounded-xl border-2 font-bold text-sm min-h-[44px] transition-colors ${
                  m === target ? "border-[#C69B3D] bg-[#F6EED8] text-[#0B192C]"
                    : "border-[#E5DEC9] bg-white text-[#566270] hover:border-[#C69B3D]"}`}>{midiToName(m)}</button>
            ))}
          </div>
        </div>
      )}

      <div className={`rounded-2xl border-2 p-6 text-center transition-colors ${inTune ? "border-[#2D6A4F] bg-[#EBF6F0]" : "border-[#E5DEC9] bg-[#FAF7EE]"}`}
        data-testid="pitch-display">
        <div className="flex items-center justify-center gap-3">
          <p className={`font-display font-bold leading-none ${inTune ? "text-[#2D6A4F]" : "text-[#0B192C]"} text-7xl sm:text-8xl`}
            data-testid="current-note">{disp ? disp.name : "—"}</p>
          <div className={`flex flex-col items-center ${!disp ? "opacity-20" : direction === "steady" ? "text-[#566270]" : "text-[#C69B3D]"}`}
            data-testid="direction-indicator" aria-label={`pitch ${direction}`}>
            <DirIcon className="w-8 h-8" aria-hidden />
            <span className="text-[10px] font-bold uppercase tracking-widest">{direction}</span>
          </div>
        </div>
        <p className="mt-2 text-sm font-semibold text-[#566270]" data-testid="hz-readout">
          {disp ? `${disp.hz.toFixed(1)} Hz` : "Sing or hum a steady note…"}
        </p>
        <div className="mt-4">
          <div className="flex justify-between text-[11px] font-bold uppercase tracking-widest text-[#566270]">
            <span>Flat</span><span className="text-[#2D6A4F]">In tune</span><span>Sharp</span>
          </div>
          <div className="relative h-4 mt-1 rounded-full bg-[#E5DEC9]" data-testid="pitch-meter">
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-[#2D6A4F]/60" aria-hidden />
            <div className={`absolute top-1/2 w-5 h-5 rounded-full border-2 ${inTune ? "bg-[#2D6A4F] border-[#2D6A4F]" : "bg-[#C69B3D] border-[#8a6a10]"}`}
              style={{ left: `${meterPct}%`, transform: "translate(-50%, -50%)", transition: "left 140ms ease-out" }}
              data-testid="pitch-meter-marker" />
          </div>
          <p className="mt-2 text-sm font-bold text-[#566270]" data-testid="cents-readout">
            {cents === null ? "" : `${cents > 0 ? "+" : ""}${cents} cents`}
          </p>
          <p className={`mt-1 text-lg font-display font-bold ${inTune ? "text-[#2D6A4F]" : "text-[#8a6a10]"}`}
            data-testid="pitch-feedback">{feedback}</p>
          {listening && !heardRecently && (
            <p className="mt-1 text-sm font-semibold text-[#A62B2B]" data-testid="pitch-no-voice">
              I can't hear you — sing a steady “ooo” into the microphone.</p>
          )}
        </div>
        <canvas ref={canvasRef} width={640} height={120} className="w-full mt-3 rounded-xl bg-white border border-[#E5DEC9]"
          data-testid="pitch-trace" aria-label="Your pitch over the last few seconds" />
      </div>

      <p className="text-xs text-[#566270]" data-testid="tuning-readout">Tuning: A = {refA4} Hz (set by your choir director)</p>

      <div className="flex gap-3">
        {!listening ? (
          <button onClick={start} data-testid="start-listening-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-[#0B192C] text-[#FAF7EE] font-bold text-sm min-h-[48px]">
            <Mic className="w-4 h-4" aria-hidden /> Start Listening
          </button>
        ) : (
          <button onClick={stop} data-testid="stop-listening-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-[#7A1C2E] text-[#FAF7EE] font-bold text-sm min-h-[48px]">
            <Square className="w-4 h-4" aria-hidden /> Stop
          </button>
        )}
      </div>
      <p className="text-[11px] text-[#566270] italic">{MIC_NOTE}</p>
    </div>
  );
}
